import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { Plus, X, GitBranch, Zap, Trash2, ChevronRight, BookOpen, Download, Check, Users } from "lucide-react";
import { useDocusealTemplates } from "../hooks/useDocusealTemplates";

const JOB_STATUSES = ["todo", "in_progress", "review", "done"];
const TRIGGERS = [
  { value: "on_status_change", label: "When job status changes" },
  { value: "on_created", label: "When job is created" },
];
const ACTIONS = [
  { value: "notify", label: "Send a notification" },
  { value: "set_status", label: "Move job to status" },
];

// Role catalog — keep in sync with backend `STAGE_ROLES` in models.py
const STAGE_ROLES = [
  { value: "", label: "— unassigned —" },
  { value: "accountant", label: "Accountant" },
  { value: "bookkeeper", label: "Bookkeeper" },
  { value: "payroll_specialist", label: "Payroll Specialist" },
  { value: "tax_specialist", label: "Tax Specialist" },
  { value: "admin", label: "Admin" },
  { value: "client_manager", label: "Client Manager" },
  { value: "partner", label: "Partner" },
];

// Group a template's stages by their `section` label — preserves stage order
// but yields a flat [{section, items:[stage,...]}, …] shape the UI iterates on.
function groupSections(stages) {
  const groups = [];
  let current = null;
  for (const s of stages || []) {
    const sec = s.section || "Ungrouped";
    if (!current || current.section !== sec) {
      current = { section: sec, items: [] };
      groups.push(current);
    }
    current.items.push(s);
  }
  return groups;
}

export default function Workflows() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, {} = new, {template_id,...} = edit
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [instancesOf, setInstancesOf] = useState(null);  // v68.9 — { template } selected for instances drawer

  const reload = async () => {
    setLoading(true);
    const { data } = await axios.get(`${API_BASE}/workflows`);
    setRows(data); setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this template? Existing jobs spawned from it are unaffected.")) return;
    await axios.delete(`${API_BASE}/workflows/${id}`);
    reload();
  };

  const installedKeys = new Set(rows.map((r) => r.blueprint_key).filter(Boolean));

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="workflows-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="overline mb-2">Workflow automation</div>
          <h1 className="font-heading text-4xl" style={{ fontWeight: 500 }}>Templates</h1>
          <p className="text-[var(--ink-2)] mt-2 max-w-xl">
            Encode your firm&apos;s playbooks. Each template carries stages + automators that fire as jobs move.
          </p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setLibraryOpen(true)} className="btn-secondary" data-testid="workflow-library-btn">
            <BookOpen size={14} /> Browse template library
          </button>
          <button onClick={() => setEditing({})} className="btn-primary" data-testid="workflow-create-btn">
            <Plus size={14} /> New template
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-[var(--ink-3)]">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card p-10 text-center text-[var(--ink-2)]" data-testid="workflows-empty">
          <GitBranch size={28} className="mx-auto mb-3 text-[var(--ink-3)]" />
          No templates yet. Create your first to start automating.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {rows.map((t) => {
            const sections = new Set((t.stages || []).map((s) => s.section).filter(Boolean));
            const clientTasks = (t.stages || []).filter((s) => s.is_client_request).length;
            return (
              <div key={t.template_id} className="module-card" data-testid={`workflow-card-${t.template_id}`}>
                <div className="flex items-start justify-between mb-3 gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      {t.blueprint_key && (
                        <span className="badge badge-outline text-[9px]" data-testid={`workflow-seed-badge-${t.template_id}`}>
                          <BookOpen size={9} className="inline mr-0.5" /> library
                        </span>
                      )}
                      {t.work_type && <span className="badge text-[9px]">{t.work_type}</span>}
                      {t.service_type && <span className="badge text-[9px]" style={{ background: "var(--subtle)" }}>{t.service_type}</span>}
                    </div>
                    <h3 className="font-heading text-xl text-[var(--ink)] leading-tight" style={{ fontWeight: 500 }}>{t.name}</h3>
                    {t.external_name && <p className="text-[11px] text-[var(--ink-3)] mt-0.5 italic">Client-facing: {t.external_name}</p>}
                    <p className="text-sm text-[var(--ink-2)] mt-1.5 leading-snug">{t.description || <span className="text-[var(--ink-3)]">No description</span>}</p>
                  </div>
                  <button onClick={() => onDelete(t.template_id)} className="text-[var(--ink-3)] hover:text-[var(--accent)]" data-testid={`workflow-delete-${t.template_id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className="badge badge-outline">{t.stages?.length || 0} stages</span>
                  {sections.size > 0 && <span className="badge badge-outline">{sections.size} sections</span>}
                  {clientTasks > 0 && <span className="badge" style={{ background: "var(--moss)", color: "var(--paper)" }}><Users size={10} className="inline mr-1" />{clientTasks} client tasks</span>}
                  <span className="badge badge-accent"><Zap size={10} className="inline mr-1" />{t.automators?.length || 0} automators</span>
                  {t.default_budget_minutes ? <span className="badge">{Math.round(t.default_budget_minutes/60*10)/10}h budget</span> : null}
                  {typeof t.instance_count === "number" && t.instance_count > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); setInstancesOf(t); }}
                      className="badge cursor-pointer hover:opacity-80"
                      style={{ background: "var(--ink)", color: "var(--paper)" }}
                      data-testid={`workflow-instances-badge-${t.template_id}`}>
                      {t.instance_count} job{t.instance_count === 1 ? "" : "s"} in flight →
                    </button>
                  )}
                </div>
                <div className="space-y-1.5 mb-4">
                  {(t.stages || []).slice(0, 4).map((s, i) => (
                    <div key={`${t.template_id}-${i}`} className="flex items-center gap-2 text-[12px]">
                      <span className="font-mono text-[var(--ink-3)]">{String(i+1).padStart(2,"0")}</span>
                      <span className="text-[var(--ink-2)] truncate flex-1">{s.name}</span>
                      {s.due_offset_days != null && <span className="text-[10px] text-[var(--ink-3)]">+{s.due_offset_days}d</span>}
                      <span className="overline text-[9px]">{(s.target_status || "todo").replace("_"," ")}</span>
                    </div>
                  ))}
                  {(t.stages || []).length > 4 && <div className="text-[11px] text-[var(--ink-3)] pl-7">+{t.stages.length - 4} more</div>}
                </div>
                <button onClick={() => setEditing(t)} className="btn-ghost w-full justify-between" data-testid={`workflow-edit-${t.template_id}`}>
                  Edit template <ChevronRight size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing !== null && (
        <TemplateEditor
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      {libraryOpen && (
        <LibraryDrawer
          installedKeys={installedKeys}
          onClose={() => setLibraryOpen(false)}
          onInstalled={() => reload()}
        />
      )}

      {instancesOf && (
        <InstancesDrawer
          template={instancesOf}
          onClose={() => setInstancesOf(null)}
        />
      )}
    </div>
  );
}

function TemplateEditor({ initial, onClose, onSaved }) {
  const isNew = !initial?.template_id;
  const [form, setForm] = useState({
    name: initial?.name || "",
    external_name: initial?.external_name || "",
    description: initial?.description || "",
    work_type: initial?.work_type || "",
    service_type: initial?.service_type || "",
    default_budget_minutes: initial?.default_budget_minutes || 0,
    stages: initial?.stages?.length ? initial.stages : [{ name: "Stage 1", target_status: "todo", budget_minutes: 30 }],
    automators: initial?.automators || [],
    budget_by_role: initial?.budget_by_role || [],
  });
  const [error, setError] = useState(""); const [saving, setSaving] = useState(false);
  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const addStage = () => setField("stages")([...form.stages, { name: `Stage ${form.stages.length+1}`, target_status: "todo", budget_minutes: 15 }]);
  const updStage = (i, k, v) => setField("stages")(form.stages.map((s, idx) => idx===i ? { ...s, [k]: v } : s));
  const delStage = (i) => setField("stages")(form.stages.filter((_, idx) => idx!==i));

  const addAuto = () => setField("automators")([...form.automators, {
    label: "New automator", trigger: "on_status_change",
    trigger_params: { to_status: "review" },
    action: "notify",
    action_params: { kind: "info", title: "Job advanced", body: "" },
  }]);
  const updAuto = (i, fn) => setField("automators")(form.automators.map((a, idx) => idx===i ? fn(a) : a));
  const delAuto = (i) => setField("automators")(form.automators.filter((_, idx) => idx!==i));

  // v68.17 — lazily fetch DocuSeal templates only when at least one stage
  // is configured with the DocuSeal provider (avoids a network call for
  // firms that only use the built-in typed signature).
  const anyDocuseal = form.stages.some((s) => s.esign_config?.provider === "docuseal");
  const dsTemplates = useDocusealTemplates(anyDocuseal);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      const body = {
        ...form,
        external_name: form.external_name || null,
        work_type: form.work_type || null,
        service_type: form.service_type || null,
        default_budget_minutes: Number(form.default_budget_minutes) || 0,
      };
      if (isNew) await axios.post(`${API_BASE}/workflows`, body);
      else await axios.patch(`${API_BASE}/workflows/${initial.template_id}`, body);
      onSaved();
    } catch (err) { setError(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="workflow-modal"
         style={{ background: "rgba(26,24,20,0.55)" }}>
      <div className="bg-[var(--paper)] border border-[var(--border)] rounded-md max-w-3xl w-full p-8 my-10">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="overline">{isNew ? "New template" : "Edit template"}</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>
              {isNew ? "Create a workflow" : initial.name}
            </h3>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="workflow-modal-close"><X size={20} /></button>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="workflow-form">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="overline block mb-1.5">Name</label>
              <input value={form.name} onChange={(e) => setField("name")(e.target.value)} required data-testid="workflow-form-name"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
            </div>
            <div>
              <label className="overline block mb-1.5">Default budget (min)</label>
              <input type="number" value={form.default_budget_minutes} onChange={(e) => setField("default_budget_minutes")(e.target.value)} data-testid="workflow-form-budget"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="overline block mb-1.5">External name <span className="text-[var(--ink-3)] normal-case">(client-facing)</span></label>
              <input value={form.external_name} onChange={(e) => setField("external_name")(e.target.value)}
                data-testid="workflow-form-external-name"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]" />
            </div>
            <div>
              <label className="overline block mb-1.5">Work type</label>
              <select value={form.work_type} onChange={(e) => setField("work_type")(e.target.value)} data-testid="workflow-form-work-type"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]">
                <option value="">—</option>
                {["Accounting","Tax","Payroll","Advisory","Corporate Secretarial","Onboarding","Ad hoc"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1.5">Service type</label>
              <select value={form.service_type} onChange={(e) => setField("service_type")(e.target.value)} data-testid="workflow-form-service-type"
                className="w-full bg-white border border-[var(--border)] rounded-md px-3 py-2 text-[13px] focus:outline-none focus:border-[var(--ink)]">
                <option value="">—</option>
                {["Accounting","Tax & Compliance","Payroll","Advisory","Assurance","Bookkeeping","Consulting & IT","Other"].map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="overline block mb-1.5">Description</label>
            <textarea value={form.description} onChange={(e) => setField("description")(e.target.value)} rows={2} data-testid="workflow-form-description"
              className="w-full bg-white border border-[var(--border)] rounded-md px-3.5 py-2.5 text-[14px] focus:outline-none focus:border-[var(--ink)]" />
          </div>

          {/* Stages */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Stages · in order</div>
              <button type="button" onClick={addStage} className="btn-ghost" data-testid="workflow-add-stage">
                <Plus size={12} /> Add stage
              </button>
            </div>
            <div className="space-y-2">
              {form.stages.map((s, i) => (
                <div key={`stage-${i}`} className="bg-white border border-[var(--border)] rounded-md p-3 space-y-2" data-testid={`workflow-stage-${i}`}>
                  <div className="flex gap-2 items-center">
                    <span className="font-mono text-[11px] text-[var(--ink-3)] w-6">{String(i+1).padStart(2,"0")}</span>
                    <input value={s.name} onChange={(e) => updStage(i, "name", e.target.value)} placeholder="Stage name" required data-testid={`workflow-stage-name-${i}`}
                      className="flex-1 bg-transparent border-b border-[var(--border)] px-1 py-1 text-[13px] focus:outline-none focus:border-[var(--ink)]" />
                    <select value={s.target_status} onChange={(e) => updStage(i, "target_status", e.target.value)} data-testid={`workflow-stage-status-${i}`}
                      className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[12px] focus:outline-none">
                      {JOB_STATUSES.map((st) => <option key={st} value={st}>{st.replace("_"," ")}</option>)}
                    </select>
                    <input type="number" value={s.budget_minutes} onChange={(e) => updStage(i, "budget_minutes", Number(e.target.value))} data-testid={`workflow-stage-budget-${i}`}
                      className="w-16 bg-white border border-[var(--border)] rounded px-2 py-1 text-[12px] text-right focus:outline-none" />
                    <span className="text-[11px] text-[var(--ink-3)]">m</span>
                    <button type="button" onClick={() => delStage(i)} className="text-[var(--ink-3)] hover:text-[var(--accent)]" data-testid={`workflow-stage-delete-${i}`}>
                      <X size={14} />
                    </button>
                  </div>
                  <div className="flex gap-2 items-center pl-8 flex-wrap">
                    <input value={s.section || ""} onChange={(e) => updStage(i, "section", e.target.value || null)} placeholder="Section (optional group heading)"
                      data-testid={`workflow-stage-section-${i}`}
                      className="flex-1 min-w-[180px] bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] focus:outline-none focus:border-[var(--ink)]" />
                    <select value={s.role || ""} onChange={(e) => updStage(i, "role", e.target.value || null)} data-testid={`workflow-stage-role-${i}`}
                      className="bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] focus:outline-none">
                      {STAGE_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                    </select>
                    <div className="flex items-center gap-1">
                      <span className="text-[10px] text-[var(--ink-3)]">Due +</span>
                      <input type="number" value={s.due_offset_days ?? ""} onChange={(e) => updStage(i, "due_offset_days", e.target.value === "" ? null : Number(e.target.value))} placeholder="—"
                        data-testid={`workflow-stage-due-${i}`}
                        className="w-14 bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] text-right focus:outline-none" />
                      <span className="text-[10px] text-[var(--ink-3)]">d</span>
                    </div>
                    <label className="flex items-center gap-1 text-[10px] text-[var(--ink-2)] cursor-pointer">
                      <input type="checkbox" checked={!!s.is_client_request} onChange={(e) => updStage(i, "is_client_request", e.target.checked)}
                        data-testid={`workflow-stage-client-${i}`} />
                      client request
                    </label>
                  </div>
                  {s.is_client_request && (
                    <textarea value={s.client_prompt || ""} onChange={(e) => updStage(i, "client_prompt", e.target.value || null)} placeholder="What to ask the client…"
                      rows={2} data-testid={`workflow-stage-prompt-${i}`}
                      className="w-full ml-8 bg-white border border-[var(--border)] rounded px-2 py-1 text-[11px] focus:outline-none focus:border-[var(--ink)]"
                      style={{ width: "calc(100% - 2rem)" }} />
                  )}
                  {/* v68.16 — eSign per-stage override */}
                  <div className="pl-8">
                    <details className="text-[11px]">
                      <summary className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]">
                        eSign override {s.esign_config ? "· configured" : "· (uses firm default)"}
                      </summary>
                      <div className="mt-2 flex gap-2 flex-wrap items-center bg-[var(--bg)] rounded p-2 border border-[var(--border)]">
                        <select
                          value={s.esign_config?.provider || ""}
                          onChange={(e) => {
                            const provider = e.target.value || null;
                            const cfg = provider ? { ...(s.esign_config || {}), provider } : null;
                            updStage(i, "esign_config", cfg);
                          }}
                          data-testid={`workflow-stage-esign-provider-${i}`}
                          className="bg-white border border-[var(--border)] rounded px-2 py-1"
                        >
                          <option value="">Firm default</option>
                          <option value="builtin">Built-in (typed)</option>
                          <option value="docuseal">DocuSeal</option>
                        </select>
                        {(s.esign_config?.provider === "docuseal") && (
                          dsTemplates.error || (!dsTemplates.loading && dsTemplates.templates.length === 0) ? (
                            <input
                              type="number" placeholder="template ID"
                              value={s.esign_config?.template_id || ""}
                              onChange={(e) => {
                                const template_id = e.target.value ? Number(e.target.value) : null;
                                updStage(i, "esign_config", { ...(s.esign_config || {}), template_id });
                              }}
                              data-testid={`workflow-stage-esign-tpl-${i}`}
                              className="w-32 bg-white border border-[var(--border)] rounded px-2 py-1"
                            />
                          ) : (
                            <select
                              value={s.esign_config?.template_id || ""}
                              onChange={(e) => {
                                const template_id = e.target.value ? Number(e.target.value) : null;
                                updStage(i, "esign_config", { ...(s.esign_config || {}), template_id });
                              }}
                              data-testid={`workflow-stage-esign-tpl-${i}`}
                              className="bg-white border border-[var(--border)] rounded px-2 py-1 max-w-[220px]"
                              disabled={dsTemplates.loading}
                            >
                              <option value="">{dsTemplates.loading ? "Loading templates…" : "Select template…"}</option>
                              {dsTemplates.templates.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                          )
                        )}
                        {s.esign_config && (
                          <button type="button" onClick={() => updStage(i, "esign_config", null)}
                                  className="text-[var(--ink-3)] hover:text-[var(--accent)]"
                                  data-testid={`workflow-stage-esign-clear-${i}`}>
                            <X size={11} /> clear
                          </button>
                        )}
                      </div>
                    </details>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Automators */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="overline">Automators · rules that fire on events</div>
              <button type="button" onClick={addAuto} className="btn-ghost" data-testid="workflow-add-automator">
                <Zap size={12} /> Add rule
              </button>
            </div>
            {form.automators.length === 0 && (
              <div className="text-[12px] text-[var(--ink-3)] italic text-center py-4 border border-dashed border-[var(--border)] rounded-md">
                No automators. Click <em>Add rule</em> to create one.
              </div>
            )}
            <div className="space-y-3">
              {form.automators.map((a, i) => (
                <div key={`auto-${i}`} className="bg-white border border-[var(--border)] rounded-md p-4 space-y-2" data-testid={`workflow-automator-${i}`}>
                  <div className="flex gap-2">
                    <input value={a.label} onChange={(e) => updAuto(i, (x) => ({ ...x, label: e.target.value }))} placeholder="Label" data-testid={`workflow-automator-label-${i}`}
                      className="flex-1 bg-transparent border-b border-[var(--border)] px-1 py-1 text-[13px] font-medium focus:outline-none focus:border-[var(--ink)]" />
                    <button type="button" onClick={() => delAuto(i)} className="text-[var(--ink-3)] hover:text-[var(--accent)]" data-testid={`workflow-automator-delete-${i}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 items-center text-[12px]">
                    <select value={a.trigger} onChange={(e) => updAuto(i, (x) => ({ ...x, trigger: e.target.value, trigger_params: e.target.value === "on_status_change" ? { to_status: "review" } : {} }))} data-testid={`workflow-automator-trigger-${i}`}
                      className="bg-white border border-[var(--border)] rounded px-2 py-1.5 focus:outline-none">
                      {TRIGGERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {a.trigger === "on_status_change" && (
                      <select value={a.trigger_params?.to_status || "review"}
                        onChange={(e) => updAuto(i, (x) => ({ ...x, trigger_params: { ...x.trigger_params, to_status: e.target.value } }))}
                        data-testid={`workflow-automator-tostatus-${i}`}
                        className="bg-white border border-[var(--border)] rounded px-2 py-1.5 focus:outline-none">
                        {JOB_STATUSES.map((st) => <option key={st} value={st}>→ {st.replace("_"," ")}</option>)}
                      </select>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 items-center text-[12px]">
                    <select value={a.action} onChange={(e) => updAuto(i, (x) => ({ ...x, action: e.target.value, action_params: e.target.value === "notify" ? { kind: "info", title: "Job advanced", body: "" } : { status: "done" } }))} data-testid={`workflow-automator-action-${i}`}
                      className="bg-white border border-[var(--border)] rounded px-2 py-1.5 focus:outline-none">
                      {ACTIONS.map((act) => <option key={act.value} value={act.value}>{act.label}</option>)}
                    </select>
                    {a.action === "set_status" && (
                      <select value={a.action_params?.status || "done"}
                        onChange={(e) => updAuto(i, (x) => ({ ...x, action_params: { ...x.action_params, status: e.target.value } }))}
                        className="bg-white border border-[var(--border)] rounded px-2 py-1.5 focus:outline-none">
                        {JOB_STATUSES.map((st) => <option key={st} value={st}>→ {st.replace("_"," ")}</option>)}
                      </select>
                    )}
                  </div>
                  {a.action === "notify" && (
                    <input value={a.action_params?.title || ""} placeholder="Notification title"
                      onChange={(e) => updAuto(i, (x) => ({ ...x, action_params: { ...x.action_params, title: e.target.value } }))}
                      data-testid={`workflow-automator-title-${i}`}
                      className="w-full bg-white border border-[var(--border)] rounded px-2 py-1.5 text-[12px] focus:outline-none" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {error && <div className="text-[13px] text-[var(--accent)]" data-testid="workflow-form-error">{error}</div>}
          <div className="flex gap-3 pt-2 border-t border-[var(--border)]">
            <button type="button" onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1 justify-center" data-testid="workflow-form-submit">
              {saving ? "Saving…" : (isNew ? "Create template" : "Save changes")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// v68.8 — Library Drawer: browse & install the 13 AU workflow blueprints.
// ============================================================================
function LibraryDrawer({ installedKeys, onClose, onInstalled }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);          // blueprint currently previewing
  const [installing, setInstalling] = useState(null);    // key currently installing
  const [filterWork, setFilterWork] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/workflows/library`);
        setItems(data.blueprints || []);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, []);

  const openPreview = async (key) => {
    try {
      const { data } = await axios.get(`${API_BASE}/workflows/library/${key}`);
      setPreview(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const install = async (key) => {
    setInstalling(key);
    try {
      const { data } = await axios.post(`${API_BASE}/workflows/library/${key}/install`);
      toast.success(data.installed ? `Installed "${data.template.name}"` : `Already installed`);
      onInstalled();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setInstalling(null); }
  };

  const workTypes = Array.from(new Set(items.map((i) => i.work_type).filter(Boolean))).sort();
  const filtered = filterWork ? items.filter((i) => i.work_type === filterWork) : items;

  return (
    <div className="fixed inset-0 z-[60] bg-[var(--ink)]/40 flex items-start justify-end"
         onClick={onClose} data-testid="workflow-library-drawer">
      <div className="bg-[var(--paper)] w-full max-w-[720px] h-full overflow-y-auto border-l border-[var(--border)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[var(--paper)] border-b border-[var(--border)] px-6 py-4 flex items-start justify-between z-10">
          <div>
            <div className="overline">Template library</div>
            <h2 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>Australian workflow blueprints</h2>
            <p className="text-[12px] text-[var(--ink-2)] mt-1">Battle-tested workflows for AU accounting practices. Install once — edit freely after.</p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="workflow-library-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pt-4 pb-2 flex gap-2 flex-wrap items-center border-b border-[var(--border)]">
          <span className="overline text-[10px] text-[var(--ink-3)]">Filter by work type:</span>
          <button onClick={() => setFilterWork("")}
            className={`text-[11px] px-2.5 py-1 rounded-full border ${filterWork === "" ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"}`}
            data-testid="workflow-library-filter-all">All</button>
          {workTypes.map((w) => (
            <button key={w} onClick={() => setFilterWork(w)}
              className={`text-[11px] px-2.5 py-1 rounded-full border ${filterWork === w ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "border-[var(--border)] text-[var(--ink-2)] hover:border-[var(--ink)]"}`}
              data-testid={`workflow-library-filter-${w.toLowerCase().replace(/[^a-z]+/g,'-')}`}>{w}</button>
          ))}
        </div>

        <div className="px-6 py-4 space-y-3">
          {loading && <div className="text-[var(--ink-3)] text-[13px]">Loading library…</div>}
          {!loading && filtered.map((b) => {
            const isInstalled = installedKeys.has(b.key);
            const hours = b.default_budget_minutes ? Math.round(b.default_budget_minutes / 60 * 10) / 10 : 0;
            return (
              <div key={b.key} className="border border-[var(--border)] rounded-md p-4 bg-white" data-testid={`workflow-blueprint-${b.key}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap mb-1">
                      {b.work_type && <span className="badge badge-outline text-[9px]">{b.work_type}</span>}
                      {b.service_type && <span className="badge text-[9px]" style={{ background: "var(--subtle)" }}>{b.service_type}</span>}
                    </div>
                    <div className="font-heading text-[15px] font-medium">{b.name}</div>
                    <p className="text-[12px] text-[var(--ink-2)] mt-1 leading-snug">{b.description}</p>
                    <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] text-[var(--ink-3)]">
                      <span>· {b.stage_count} stages</span>
                      <span>· {b.section_count} sections</span>
                      {b.client_task_count > 0 && <span>· {b.client_task_count} client tasks</span>}
                      {b.automator_count > 0 && <span>· {b.automator_count} automators</span>}
                      {hours > 0 && <span>· {hours}h budget</span>}
                    </div>
                    {b.budget_by_role?.length > 0 && (
                      <div className="text-[10px] text-[var(--ink-3)] mt-1">
                        {b.budget_by_role.map((r) => `${r.role} ${Math.round(r.minutes/60*10)/10}h`).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={() => openPreview(b.key)} className="btn-ghost text-[11px] px-2.5 py-1"
                      data-testid={`workflow-blueprint-preview-${b.key}`}>Preview</button>
                    <button onClick={() => install(b.key)} disabled={isInstalled || installing === b.key}
                      className={`text-[11px] px-2.5 py-1 rounded inline-flex items-center gap-1 ${isInstalled ? "bg-[var(--subtle)] text-[var(--ink-3)] cursor-default" : "bg-[var(--ink)] text-[var(--paper)] hover:opacity-90"}`}
                      data-testid={`workflow-blueprint-install-${b.key}`}>
                      {isInstalled ? <><Check size={11}/> Installed</> : (installing === b.key ? "Installing…" : <><Download size={11}/> Install</>)}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {preview && <BlueprintPreviewOverlay blueprint={preview} onClose={() => setPreview(null)} />}
      </div>
    </div>
  );
}

// Inline preview overlay — read-only stage/section listing.
function BlueprintPreviewOverlay({ blueprint, onClose }) {
  const groups = groupSections(blueprint.stages);
  return (
    <div className="fixed inset-0 z-[70] bg-[var(--ink)]/60 flex items-center justify-center p-6"
         onClick={onClose} data-testid="workflow-blueprint-preview">
      <div className="bg-[var(--paper)] w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-md border border-[var(--border)] p-6"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="overline">Blueprint preview</div>
            <h3 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>{blueprint.name}</h3>
            {blueprint.external_name && <p className="text-[12px] text-[var(--ink-3)] italic mt-0.5">Client-facing: {blueprint.external_name}</p>}
            <p className="text-[13px] text-[var(--ink-2)] mt-2">{blueprint.description}</p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]"><X size={18}/></button>
        </div>
        <div className="space-y-4">
          {groups.map((g, gi) => (
            <div key={`grp-${gi}`}>
              <div className="overline text-[10px] text-[var(--ink-3)] mb-1.5">{g.section}</div>
              <div className="space-y-1.5">
                {g.items.map((s, i) => (
                  <div key={`${gi}-${i}`} className="border border-[var(--border)] rounded px-3 py-2 flex items-center gap-2 text-[12px]">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.is_client_request ? "bg-[var(--moss)]" : "bg-[var(--ink-3)]"}`}></span>
                    <span className="flex-1">{s.name}</span>
                    {s.role && <span className="text-[10px] text-[var(--ink-3)] uppercase">{s.role.replace("_"," ")}</span>}
                    {s.due_offset_days != null && <span className="text-[10px] text-[var(--ink-3)]">+{s.due_offset_days}d</span>}
                    {s.budget_minutes > 0 && <span className="text-[10px] text-[var(--ink-3)]">{s.budget_minutes}m</span>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// v68.9 — Instances drawer: every job spawned from a given template.
// ============================================================================
function InstancesDrawer({ template, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_BASE}/workflows/${template.template_id}/instances`);
        setData(r.data);
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setLoading(false); }
    })();
  }, [template.template_id]);

  const statusColor = (status) => ({
    todo: "var(--ink-3)", in_progress: "var(--accent)",
    review: "var(--moss)", done: "var(--ink)",
  })[status] || "var(--ink-3)";

  return (
    <div className="fixed inset-0 z-[60] bg-[var(--ink)]/40 flex items-start justify-end"
         onClick={onClose} data-testid="workflow-instances-drawer">
      <div className="bg-[var(--paper)] w-full max-w-[720px] h-full overflow-y-auto border-l border-[var(--border)]"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[var(--paper)] border-b border-[var(--border)] px-6 py-4 flex items-start justify-between z-10">
          <div>
            <div className="overline">Instances</div>
            <h2 className="font-heading text-2xl mt-1" style={{ fontWeight: 500 }}>
              Jobs from &ldquo;{template.name}&rdquo;
            </h2>
            <p className="text-[12px] text-[var(--ink-2)] mt-1">
              Every job that was spawned from this template. Click a row to jump to the job.
            </p>
          </div>
          <button onClick={onClose} className="text-[var(--ink-3)] hover:text-[var(--ink)]" data-testid="workflow-instances-close">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-4">
          {loading && <div className="text-[13px] text-[var(--ink-3)]">Loading instances…</div>}
          {!loading && data?.analytics && (
            <div className="grid grid-cols-4 gap-2 mb-4" data-testid="workflow-instances-analytics">
              <AnalyticsCard label="Active" value={data.analytics.active} suffix="jobs"/>
              <AnalyticsCard label="Completed" value={data.analytics.completed} suffix="jobs"/>
              <AnalyticsCard label="Avg cycle" value={data.analytics.avg_cycle_hours} suffix="h"
                                empty="—" hint="created → done"/>
              <AnalyticsCard label="On time" value={data.analytics.on_time_pct} suffix="%"
                                empty="—" hint={data.analytics.on_time_sample
                                                ? `of ${data.analytics.on_time_sample} with a due date`
                                                : "no completed jobs yet"}/>
            </div>
          )}
          {!loading && (!data?.instances || data.instances.length === 0) && (
            <div className="text-center text-[13px] text-[var(--ink-3)] py-10 border border-dashed border-[var(--border)] rounded-md">
              No jobs spawned from this template yet.
              <div className="text-[11px] mt-1">
                Head to the Jobs page and choose this template when creating a new job.
              </div>
            </div>
          )}
          {!loading && data?.instances?.length > 0 && (
            <div className="space-y-2">
              <div className="text-[11px] text-[var(--ink-3)] mb-2">
                Showing {data.count} job{data.count === 1 ? "" : "s"}
              </div>
              {data.instances.map((inst) => (
                <a key={inst.job_id} href={`/jobs?job_id=${inst.job_id}`}
                   className="flex items-center gap-3 border border-[var(--border)] rounded-md px-3 py-2 hover:border-[var(--ink)] transition"
                   data-testid={`workflow-instance-${inst.job_id}`}>
                  <div className="w-2 h-2 rounded-full shrink-0" style={{ background: statusColor(inst.status) }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-medium text-[var(--ink)] truncate">{inst.title}</div>
                    <div className="text-[11px] text-[var(--ink-3)] font-mono truncate">
                      {inst.client_name || "no client"} · {new Date(inst.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <span className="overline text-[9px] shrink-0">{(inst.status || "").replace(/_/g," ")}</span>
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AnalyticsCard({ label, value, suffix, empty = "0", hint }) {
  const display = (value === null || value === undefined) ? empty : value;
  return (
    <div className="border border-[var(--border)] rounded-md p-3 bg-white" data-testid={`analytics-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="overline text-[9px] text-[var(--ink-3)]">{label}</div>
      <div className="font-heading text-2xl leading-none mt-1" style={{ fontWeight: 500 }}>
        {display}
        {(value !== null && value !== undefined && suffix) && (
          <span className="text-[11px] text-[var(--ink-3)] font-mono ml-1">{suffix}</span>
        )}
      </div>
      {hint && <div className="text-[10px] text-[var(--ink-3)] mt-1 leading-tight">{hint}</div>}
    </div>
  );
}

